import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/context/CartContext';

const EDKBuyButton = () => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart({
      id: 'edk-complete-kit',
      name: t('edk_purch_kit_label'),
      price: 79.95,
      type: 'kit',
      description: t('edk_purch_includes_label'),
      image: 'https://public.readdy.ai/ai/img_res/3bbe4ef6f800b1c88b3e05245c1e4f77.jpg',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <button
        onClick={handleAddToCart}
        className={`w-full inline-flex items-center justify-center gap-3 px-10 py-4 font-black text-base rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
          added
            ? 'bg-emerald-600 text-white'
            : 'bg-gradient-to-r from-orange-600 to-red-700 text-white hover:opacity-90'
        }`}
      >
        {added ? (
          <>
            <i className="ri-check-line text-base" />
            {t('edk_purch_added_btn')}
          </>
        ) : (
          <>
            <i className="ri-shopping-bag-line text-base" />
            {t('edk_purch_add_btn')}
          </>
        )}
      </button>
      <p className="text-white/30 text-xs flex items-center gap-1.5">
        <i className="ri-shield-check-line text-orange-500" />
        {t('edk_purch_safe_pay')}
      </p>
    </div>
  );
};

export const EDKPurchase = () => {
  const { t } = useTranslation();

  const testimonials = [
    { nameKey: 'edk_purch_t1_name', textKey: 'edk_purch_t1_text', sitKey: 'edk_purch_t1_sit', rating: 5 },
    { nameKey: 'edk_purch_t2_name', textKey: 'edk_purch_t2_text', sitKey: 'edk_purch_t2_sit', rating: 5 },
    { nameKey: 'edk_purch_t3_name', textKey: 'edk_purch_t3_text', sitKey: 'edk_purch_t3_sit', rating: 5 },
    { nameKey: 'edk_purch_t4_name', textKey: 'edk_purch_t4_text', sitKey: 'edk_purch_t4_sit', rating: 5 },
  ];

  const features = [
    { icon: 'ri-book-open-line', key: 'edk_purch_feat1' },
    { icon: 'ri-leaf-line', key: 'edk_purch_feat2' },
    { icon: 'ri-sun-line', key: 'edk_purch_feat3' },
    { icon: 'ri-fire-line', key: 'edk_purch_feat4' },
  ];

  const guarantees = [
    { icon: 'ri-shield-check-line', key: 'edk_purch_g1' },
    { icon: 'ri-truck-line', key: 'edk_purch_g2' },
    { icon: 'ri-global-line', key: 'edk_purch_g3' },
    { icon: 'ri-customer-service-line', key: 'edk_purch_g4' },
  ];

  return (
    <>
      {/* Testimonials */}
      <section className="w-full py-20 bg-[#080000]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('edk_purch_test_badge')}</p>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              {t('edk_purch_test_title')}{' '}
              <span className="text-transparent bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text">
                {t('edk_purch_test_title2')}
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map(item => (
              <div key={item.nameKey} className="bg-[#100000]/60 border border-orange-500/15 rounded-2xl p-6">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-orange-400 text-xs" />
                    </div>
                  ))}
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-5 italic">&ldquo;{t(item.textKey)}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-700/30 border border-orange-500/20">
                      <i className="ri-user-line text-orange-400 text-xs" />
                    </div>
                    <span className="text-white font-semibold text-xs">{t(item.nameKey)}</span>
                  </div>
                  <span className="text-orange-400/60 text-xs px-2 py-0.5 bg-orange-900/20 border border-orange-500/15 rounded-full">
                    {t(item.sitKey)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Purchase CTA */}
      <section className="w-full py-24 bg-[#060000] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dramatic%20fire%20ritual%20purification%20ceremony%20dark%20background%20intense%20orange%20red%20flames%20smoke%20destruction%20of%20evil%20forces%20ancient%20shaman%20ritual%20fire%20burning%20herbs%20dark%20atmospheric%20deep%20black%20background%20cinematic%20dramatic%20powerful%20energy&width=1920&height=700&seq=edkcta1&orientation=landscape"
            alt="Destroy Kit CTA"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#060000]/90" />
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-4">{t('edk_purch_cta_badge')}</p>
            <h2 className="text-4xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {t('edk_purch_cta_title')}<br />
              <span className="text-transparent bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text">
                {t('edk_purch_cta_title2')}
              </span>
            </h2>
            <p className="text-white/45 text-sm lg:text-base max-w-xl mx-auto leading-relaxed mb-10">
              {t('edk_purch_cta_sub')}
            </p>

            {/* Price card */}
            <div className="inline-flex flex-col items-center bg-[#100000]/80 backdrop-blur-xl border border-orange-500/25 rounded-3xl p-8 mb-10 mx-auto w-full max-w-md">
              <span className="text-orange-300/60 text-xs uppercase tracking-widest mb-2">
                {t('edk_purch_kit_label')}
              </span>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-white/30 text-lg line-through">{t('edk_purch_old_price')}</span>
                <span className="text-5xl font-black text-orange-400">€79,95</span>
              </div>
              <span className="text-white/30 text-xs mb-6">
                {t('edk_purch_includes_label')}
              </span>

              <div className="grid grid-cols-2 gap-3 w-full mb-6">
                {features.map(item => (
                  <div
                    key={item.key}
                    className="flex items-center gap-2 bg-orange-900/15 border border-orange-500/15 rounded-xl px-3 py-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-orange-400 text-xs`} />
                    </div>
                    <span className="text-white/55 text-xs">{t(item.key)}</span>
                  </div>
                ))}
              </div>

              <EDKBuyButton />
            </div>

            {/* Guarantees */}
            <div className="flex flex-wrap justify-center gap-6 text-white/30 text-xs">
              {guarantees.map(item => (
                <span key={item.key} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`${item.icon} text-orange-500`} />
                  </div>
                  {t(item.key)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
